import { For, Show, createMemo, type JSX } from "solid-js";
import { summarizeActivity } from "../../api/adapters";
import type { MeterRecord } from "../../model";
import type { AsyncPanel } from "../../state/async";
import { formatCompact, formatDate, formatNumber } from "../../utils/format";
import { Modal } from "../../components/Modal";
import { PanelState } from "../../components/PanelState";

interface ActivityPanelProps {
  panel: AsyncPanel<MeterRecord[]>;
  onOpenRecords: () => void;
}

function statusClass(status: number): string {
  if (status >= 200 && status < 300) return "good";
  if (status >= 400 && status < 500) return "warning";
  return "danger";
}

export function ActivityPanel(props: ActivityPanelProps): JSX.Element {
  const records = createMemo(() => props.panel.state().data ?? []);
  const sorted = createMemo(() => [...records()].sort((left, right) => left.startedAtMS - right.startedAtMS));
  const summary = createMemo(() => summarizeActivity(records()));
  const maximumTokens = createMemo(() =>
    Math.max(1, ...records().map((item) => item.tokens.total ?? 0)),
  );

  return (
    <section class="bento bento--activity" aria-labelledby="activity-heading">
      <header class="bento__header">
        <div>
          <p class="eyebrow">Request Activity · 最近 100 条已记录请求</p>
          <h2 id="activity-heading">请求活动与最近用量</h2>
        </div>
        <button type="button" class="button button--ghost" onClick={props.onOpenRecords}>
          查看记录
        </button>
      </header>

      <div class="metric-row metric-row--activity">
        <div class="metric"><span>请求</span><strong>{formatNumber(summary().records)}</strong></div>
        <div class="metric"><span>成功</span><strong>{formatNumber(summary().success)}</strong></div>
        <div class="metric"><span>失败</span><strong>{formatNumber(summary().failures)}</strong></div>
        <div class="metric"><span>已知 Token</span><strong>{formatCompact(summary().totalTokens)}</strong></div>
        <div class="metric">
          <span>Token 覆盖率</span>
          <strong>
            {summary().tokenCoveragePercent === null
              ? "未知"
              : `${summary().tokenCoveragePercent?.toFixed(0)}%`}
          </strong>
        </div>
      </div>

      <Show
        when={records().length > 0}
        fallback={
          <div class="empty-state">
            <strong>暂无 Request Meter 记录</strong>
            <span>新请求产生后，这里会显示 best-effort 活动样本。</span>
          </div>
        }
      >
        <button
          type="button"
          class="activity-chart"
          aria-label="打开最近请求记录"
          onClick={props.onOpenRecords}
        >
          <svg viewBox="0 0 960 168" role="img" aria-label="最近请求按时间排列，标记高度代表已上报 Token">
            <line class="activity-chart__baseline" x1="16" y1="144" x2="944" y2="144" />
            <For each={sorted()}>
              {(item, index) => {
                const x = () =>
                  sorted().length === 1 ? 480 : 18 + (index() / (sorted().length - 1)) * 924;
                const known = () => item.usageStatus === "reported" && item.tokens.total !== null;
                const height = () =>
                  known() ? 10 + Math.sqrt((item.tokens.total ?? 0) / maximumTokens()) * 112 : 7;
                return (
                  <line
                    class={`activity-chart__mark activity-chart__mark--${statusClass(item.httpStatus)} ${
                      known() ? "" : "activity-chart__mark--unknown"
                    }`}
                    x1={x()}
                    x2={x()}
                    y1={144 - height()}
                    y2="144"
                  >
                    <title>
                      {`${formatDate(item.startedAtMS)} · ${item.httpStatus} · ${
                        item.tokens.total === null ? "Token 未上报" : `${formatNumber(item.tokens.total)} tokens`
                      }`}
                    </title>
                  </line>
                );
              }}
            </For>
          </svg>
          <span class="activity-chart__legend">
            <span><i class="legend-dot legend-dot--good" />2xx</span>
            <span><i class="legend-dot legend-dot--warning" />4xx</span>
            <span><i class="legend-dot legend-dot--danger" />5xx / failure</span>
            <span><i class="legend-dot legend-dot--unknown" />Token 未上报</span>
          </span>
        </button>
      </Show>
      <div class="usage-breakdown" aria-label="已上报 Token 分类">
        <span>Input <strong>{formatCompact(summary().inputTokens)}</strong></span>
        <span>Output <strong>{formatCompact(summary().outputTokens)}</strong></span>
        <span>Cache read <strong>{formatCompact(summary().cacheReadTokens)}</strong></span>
        <span>Cache write <strong>{formatCompact(summary().cacheWriteTokens)}</strong></span>
      </div>
      <PanelState state={props.panel.state()} onRetry={() => void props.panel.refresh()} />
    </section>
  );
}

interface ActivityRecordsModalProps {
  records: MeterRecord[];
  onClose: () => void;
}

export function ActivityRecordsModal(props: ActivityRecordsModalProps): JSX.Element {
  return (
    <Modal title="最近请求记录" eyebrow="Request Activity · best-effort" onClose={props.onClose} size="wide">
      <p class="modal-intro">
        这里只展示最近 100 条已记录请求，不代表完整时间窗口或账单。Token 缺失不会被计算为 0。
      </p>
      <div class="table-scroll table-scroll--modal">
        <table>
          <thead>
            <tr>
              <th>时间</th>
              <th>状态</th>
              <th>路由</th>
              <th>模型</th>
              <th>耗时</th>
              <th>TTFB</th>
              <th>Token</th>
              <th>Relay</th>
              <th>Request ID</th>
            </tr>
          </thead>
          <tbody>
            <For each={props.records}>
              {(item) => (
                <tr>
                  <td>{formatDate(item.startedAtMS)}</td>
                  <td><span class={`http-status http-status--${statusClass(item.httpStatus)}`}>{item.httpStatus}</span></td>
                  <td>{item.routeKind}</td>
                  <td>{item.model ?? "未知"}</td>
                  <td>{formatNumber(item.durationMS)} ms</td>
                  <td>{item.ttfbMS === null ? "—" : `${formatNumber(item.ttfbMS)} ms`}</td>
                  <td>
                    {item.usageStatus === "reported" && item.tokens.total !== null
                      ? formatNumber(item.tokens.total)
                      : `未知（${item.usageStatus}）`}
                  </td>
                  <td><code>{item.relayID}</code></td>
                  <td><code>{item.requestID}</code></td>
                </tr>
              )}
            </For>
          </tbody>
        </table>
      </div>
    </Modal>
  );
}
